import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { createStyles, palette } from "../../../styles/theme";
import { SpecsInputField } from "../SpecsEditorFields/index";
import { useSpecsPageContext } from "../SpecsPageContext";
import { SpecsPageView } from "../types";
import type { CreateSpecDraftInput } from "../useSpecState";

const styles = createStyles({
  headerWrapper: {
    textAlign: "center",
    border: `1px solid ${palette.grey.lightGrey}`,
    bgcolor: palette.grey.lightBackground,
    width: "100%",
  },
  headerContent: {
    px: 2,
    py: 1,
    flexWrap: "wrap",
    maxWidth: "1200px",
    textAlign: "left",
    margin: "auto",
  },
});

export default function SpecsHeader() {
  const {
    specType,
    title,
    emptyLabel,
    createDraftFields,
    options,
    selectedValue,
    loading,
    currentView,
    draftJson,
    saveLoading,
    publishLoading,
    onSelect,
    onToggleView,
    onSave,
    onPublish,
    onCreateDraft,
  } = useSpecsPageContext();
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [createDraftValues, setCreateDraftValues] =
    React.useState<CreateSpecDraftInput>({ specName: "" });
  const [createError, setCreateError] = React.useState("");
  const selectedOption = options.find((option) => option.id === selectedValue);

  const closeCreateDialog = React.useCallback(() => {
    setCreateDialogOpen(false);
    setCreateDraftValues({ specName: "" });
    setCreateError("");
  }, []);

  const submitCreateDraft = React.useCallback(() => {
    try {
      onCreateDraft(createDraftValues);
      closeCreateDialog();
    } catch (error) {
      setCreateError(
        error instanceof Error ? error.message : `Failed to create ${specType}`
      );
    }
  }, [closeCreateDialog, createDraftValues, onCreateDraft, specType]);

  const updateCreateDraftValue = React.useCallback(
    (name: keyof CreateSpecDraftInput, value: string) => {
      setCreateDraftValues((current) => ({
        ...current,
        [name]: value,
      }));
      setCreateError("");
    },
    []
  );

  return (
    <Box sx={styles.headerWrapper}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={styles.headerContent}
      >
        {currentView === SpecsPageView.History ? (
          <IconButton
            size="small"
            onClick={onToggleView}
            disabled={!selectedValue}
            aria-label="Back to editor"
            sx={{ ml: -0.5 }}
          >
            <ArrowBackOutlinedIcon fontSize="small" />
          </IconButton>
        ) : null}

        <Typography variant="h2" sx={{ mr: 1 }}>
          {title}
        </Typography>

        <Autocomplete
          size="small"
          disableClearable
          autoHighlight
          openOnFocus
          disabled={loading || options.length === 0}
          options={options}
          value={selectedOption}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText={emptyLabel}
          onChange={(_event, option) => {
            onSelect(option.id);
          }}
          sx={{
            minWidth: 260,
            "& .MuiAutocomplete-inputRoot": {
              py: "0 !important",
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder={emptyLabel}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
              }}
              inputProps={{
                ...params.inputProps,
                autoComplete: "off",
              }}
            />
          )}
        />

        <Box sx={{ flex: 1 }} />

        {currentView === SpecsPageView.Editor ? (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setCreateDialogOpen(true)}
            disabled={loading}
          >
            New
          </Button>
        ) : null}

        {currentView === SpecsPageView.Editor ? (
          <Button
            variant="outlined"
            size="small"
            onClick={onToggleView}
            disabled={!selectedValue}
          >
            Historique
          </Button>
        ) : null}

        {currentView === SpecsPageView.Editor ? (
          <>
            <Button
              variant="outlined"
              size="small"
              onClick={onSave}
              disabled={!draftJson.trim() || saveLoading || publishLoading}
            >
              {saveLoading ? "Sauvegarde..." : "Sauvegarder"}
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={onPublish}
              disabled={!draftJson.trim() || saveLoading || publishLoading}
            >
              {publishLoading ? "Publication..." : "Publier"}
            </Button>
          </>
        ) : null}
      </Stack>

      <Dialog
        open={createDialogOpen}
        onClose={closeCreateDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Nouveau {specType}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {createDraftFields.map((field, fieldIndex) => (
              <SpecsInputField
                key={field.name}
                label={field.label}
                value={createDraftValues[field.name] ?? ""}
                onChange={(event) => {
                  updateCreateDraftValue(field.name, event.target.value);
                }}
                autoFocus={fieldIndex === 0}
                fullWidth
                size="small"
                error={fieldIndex === 0 && !!createError}
                helperText={
                  fieldIndex === 0 ? createError || " " : field.helperText
                }
                InputProps={{ disableUnderline: true }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    submitCreateDraft();
                  }
                }}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog}>Annuler</Button>
          <Button variant="contained" onClick={submitCreateDraft}>
            Créer un brouillon
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
